import { DS } from '../../design-system'
import { StatusBar, StatusBarStyle } from 'expo-status-bar'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { Platform, StyleSheet, TouchableOpacity, useWindowDimensions, View } from 'react-native'
import { observer } from 'mobx-react-lite'
import { useStore } from '../../models'
import { PlayerCards, TutorialTable } from '../../components'
import { PlayerInfo } from '../../components/ANewDesign/PlayerHand/PlayerInfo'
import { translate } from '../../i18n'
import { color, fontFamily } from '../../theme'
import { DSButtonType } from '../../design-system/Button'
import { ActionHint } from '@ppwr/poker'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import FirstGameWelcomeScreen from './MyFirstGameWelcomeScreen'
import MyFirstGameBackground from './MyFirstGameBackground'
import MyFirstGameOverlay, { ContentLayout } from './MyFirstGameOverlay'
import MyFirstGameStepper from './MyFirstGameStepper'
import MyFirstGameStepNames from '../../models/game/first-game/MyFirstGameStepNames'
import { automaticStages, stagesWhereCallIsEnabled } from '../../models/game/first-game/MyFirstGameUtils'
import useFirstGameHardwareBackButtonHandler from './useFirstGameHardwareBackButtonHandler'
import { MyFirstGameWinScreen } from './MyFirstGameWinScreen'
import MyFirstGameSteps from '../../models/game/first-game/MyFirstGameSteps'
import { BottomModalTypes } from '../../components/ANewDesign/BottomModalContent/Content'
import { palette } from '../../theme/palette'
import { BottomModalContent } from '../../components/ANewDesign/BottomModalContent'
import { BottomSheetModal } from '@gorhom/bottom-sheet'
import { ReduceMotion } from 'react-native-reanimated'
import { useGameBottomModal } from '../new-design/hooks/useGameBottomModals'
import { useMaxWidth, WebWrapper } from '@/components/WebWrapper'
import { LastVisitedTab } from '@/models/session/session-user'
import { useResponsive } from '@/screens/new-design/hooks'
import {
  LayoutShiftAnchor,
  LayoutShiftContent,
  LayoutShiftProvider,
  useLayoutShiftRegistry
} from '@/hooks/useLayoutShiftRegistry'
import { events } from '@/models/analytics/analytics-events'

const isWeb = Platform.OS === 'web'

type ColorAndIcon = {
  icon: ReactElement
  type: DSButtonType
}
const ActionColorAndIcons: Partial<Record<ActionHint, ColorAndIcon>> = {
  ALLIN: {
    icon: <DS.Icon name="Arrow--top-right" size={16} color="Global/White" />,
    type: 'On Light'
  },
  BET: {
    icon: <DS.Icon name="Arrow--top-right" size={16} color="Global/White" />,
    type: 'On Light'
  },
  CALL: {
    icon: <DS.Icon name="Arrow---right" size={16} color="Global/Black" />,
    type: 'On Dark'
  },
  CHECK: {
    icon: <DS.Icon name="Arrow---right" size={16} color="Global/Black" />,
    type: 'On Dark'
  },
  FOLD: {
    icon: <DS.Icon name="Arrow--bottom-right" size={16} color="Global/White" />,
    type: 'On Light'
  },
  RAISE: {
    icon: <DS.Icon name="Arrow--top-right" size={16} color="Global/White" />,
    type: 'On Light'
  }
}

const MyFirstGameScreen = observer(() => {
  const { width } = useWindowDimensions()
  const isSmallDevice = useMemo(() => width < 380, [width])
  const insets = useSafeAreaInsets()
  const maxWidth = useMaxWidth()

  const {
    game: {
      isMyTurn,
      firstGameStage,
      myPlayer,
      stageInternalName,
      goToNextFirstGameStep,
      setIsMyFirstGame,
      setChipsAnimationStarted
    },
    session: { lastVisitedTab, updateUserInfo, userInfo },
    analytics: { setEvent }
  } = useStore()

  const {
    bottomModalContent,
    bottomSheetModalRef,
    snapPoints,
    enableDismissOnClose,
    closeBottomModal,
    renderBackdrop
  } = useGameBottomModal()
  const [contentLayout, setContentLayout] = useState<ContentLayout | undefined>(undefined)
  const { verticalScale } = useResponsive()
  // const cardsHeight = verticalScale(24) * 3 + 50
  const cardsHeight = verticalScale(24) * 3 + 30

  const { register } = useLayoutShiftRegistry()

  useFirstGameHardwareBackButtonHandler()

  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        container: {
          backgroundColor: '#7B043D',
          flex: 1
        },
        tableContainer: {
          flex: 4,
          backgroundColor: 'transparent',
          marginBottom: '-10%'
        },
        cardsContainer: {
          paddingBottom: '15%',
          zIndex: 1
        },
        playerContainer: {
          backgroundColor: 'rgba(246, 242, 233, 1)',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          padding: 16,
          paddingBottom: 12
        },
        bottomSheetHandleStyle: {
          backgroundColor: '#F6F2E9',
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          paddingBottom: 0,
          height: 16
        },
        bottomSheetIndicatorStyle: {
          backgroundColor: 'gray',
          height: 2,
          width: 44
        },
        container2: {
          flex: 1,
          paddingHorizontal: DS.spacing[16],
          paddingTop: DS.spacing[20],
          gap: DS.spacing[8]
        },
        firstRow: {
          flexDirection: 'row',
          width: '100%'
        },
        secondRow: {
          justifyContent: 'center',
          alignItems: 'center'
        },
        playerLastActionContainer: {
          width: '100%',
          flexDirection: 'row',
          gap: DS.spacing[16],
          alignItems: 'center'
        },
        video: {
          width: 48,
          height: 48,
          borderRadius: 24,
          backgroundColor: color.palette.smokyBlack
        },
        audioIconContainer: {
          width: 48,
          height: 48,
          borderRadius: 24,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: DS.palette['Global/White'],
          borderWidth: 1,
          borderColor: DS.palette['Brand/Light Grey']
        },
        initials: {
          fontFamily: fontFamily.FeatureDeckMedium,
          fontSize: 14,
          color: color.palette.white
        },
        noPlayerInfo: {
          justifyContent: 'center'
        },
        callContainer: {
          position: 'absolute',
          zIndex: 100,
          bottom: -12,
          paddingHorizontal: 8,
          paddingVertical: 4,
          borderRadius: 48,
          borderColor: DS.palette['Brand/Light Grey'],
          borderWidth: 1,
          flexDirection: 'row',
          gap: 4,
          alignItems: 'center',
          backgroundColor: DS.palette['Global/White']
        },
        tooltipContainer: {
          position: 'absolute',
          bottom: isSmallDevice ? -40 : -56,
          width: width / 2,
          alignItems: 'center'
        }
      }),
    [isSmallDevice, width]
  )

  const markGameAsFinished = async () => {
    if (!userInfo?.myFirstGameFinish) {
      await updateUserInfo({
        myFirstGameFinish: true
      })
    }
  }

  useEffect(() => {
    if (firstGameStage > 0) {
      setEvent('First Game Step Viewed', { screen_name: stageInternalName, index: firstGameStage })
    }

    if (stageInternalName === MyFirstGameStepNames.endCard) {
      //todo: first_g_hand: 1 because later on there will be more tutorials like first game
      setEvent('First Game Completed', { first_g_hand: 1 })
      markGameAsFinished()
    }
  }, [firstGameStage, setEvent, stageInternalName])

  useEffect(() => {
    setEvent(events.gamescreen.game_screen_viewed, {
      game_kind: 'firstGame'
    })
  }, [setEvent])

  useEffect(() => {
    setChipsAnimationStarted(false)
  }, [setChipsAnimationStarted])

  useEffect(() => {
    setIsMyFirstGame(true)
    return () => {
      setIsMyFirstGame(false)
    }
  }, [setIsMyFirstGame])

  useEffect(() => {
    if (
      stageInternalName === MyFirstGameStepNames.firstRoundFinished ||
      stageInternalName === MyFirstGameStepNames.secondRoundFinished ||
      stageInternalName === MyFirstGameStepNames.thirdRoundFinished ||
      stageInternalName === MyFirstGameStepNames.fourthRoundFinished
    ) {
      setTimeout(() => {
        setChipsAnimationStarted(true)
      }, 250)
    }

    if (
      stageInternalName === MyFirstGameStepNames.estherChecks2 ||
      stageInternalName === MyFirstGameStepNames.theTurn ||
      stageInternalName === MyFirstGameStepNames.theRiver ||
      stageInternalName === MyFirstGameStepNames.theShowdown
    ) {
      setChipsAnimationStarted(false)
    }

    if (automaticStages.includes(stageInternalName)) {
      setTimeout(() => {
        goToNextFirstGameStep()
      }, 2000)
    }
  }, [stageInternalName, setChipsAnimationStarted, goToNextFirstGameStep, firstGameStage])

  const getActionText = (action: ActionHint) => {
    const text = translate(`newGameScreen.player.actionHints.${action}`)
    if (!isMyTurn) return text
    return text
  }

  const CallAmountIndicator = () => {
    let callAmount = ''

    switch (stageInternalName) {
      case MyFirstGameStepNames.userTurnCall1:
        callAmount = '300'
        break
      case MyFirstGameStepNames.userTurnCall2:
        callAmount = '675'
        break
      case MyFirstGameStepNames.userTurnCall3:
        callAmount = '1,350'
        break
      case MyFirstGameStepNames.userTurnCall4:
        callAmount = '2,700'
        break
    }

    return (
      <TouchableOpacity
        onPress={() => {
          goToNextFirstGameStep()
        }}
        style={styles.callContainer}
      >
        <DS.Icon name="Chip" size={10} color="Global/Black" />
        <DS.Text variant="Caption/Bold" color="Brand/Green">
          {callAmount}
        </DS.Text>
      </TouchableOpacity>
    )
  }

  return (
    <DS.Frame style={{ flex: 1 }}>
      <StatusBar animated={true} style={'light' as StatusBarStyle} hidden={false} />
      <MyFirstGameBackground />
      <LayoutShiftProvider style={{ flex: 1 }}>
        <WebWrapper>
          {firstGameStage > 0 && firstGameStage < MyFirstGameSteps.length - 1 && (
            <MyFirstGameOverlay contentLayout={contentLayout} />
          )}
          <DS.Frame style={{ flex: 1, marginTop: insets.top }}>
            <DS.Frame style={{ flex: 1 }}>
              <LayoutShiftAnchor name="myFirstGame.stepper" style={{ zIndex: 9 }}>
                <MyFirstGameStepper lastPage={lastVisitedTab as LastVisitedTab} />
              </LayoutShiftAnchor>

              {firstGameStage === 0 && <FirstGameWelcomeScreen />}
              {firstGameStage > 0 && firstGameStage < MyFirstGameSteps.length - 1 && (
                <DS.Frame style={{ flex: 1, zIndex: 5, elevation: 40 }}>
                  <View
                    style={{ flex: 1, paddingHorizontal: 16, paddingBottom: 16 }}
                    onLayout={(event) => {
                      if (isWeb) {
                        // @ts-ignore this is for web
                        event.nativeEvent.target?.measure((x, y, width, height, pageX, pageY) => {
                          setContentLayout({ x, y, width, height, pageX, pageY })
                        })
                        return
                      }
                      event.target?.measure((x, y, width, height, pageX, pageY) => {
                        setContentLayout({ x, y, width, height, pageX, pageY })
                      })
                    }}
                  >
                    <TutorialTable
                      onSidePotsPress={() => {
                        //not needed
                      }}
                      onPlayerCellPress={() => {
                        //not needed
                      }}
                    />
                  </View>

                  <View
                    style={[
                      styles.playerContainer,
                      {
                        flex: 1 / 5,
                        maxHeight: 160
                      }
                    ]}
                  >
                    <>
                      <View
                        style={[
                          {
                            position: 'absolute',
                            zIndex: 3,
                            left: 0,
                            right: 0,
                            top: -32,
                            marginTop: verticalScale(24) * 3 * -1
                            // opacity: 0.3
                          }
                        ]}
                        onLayout={(e) => register('myFirstGame.playerCards', e)}
                      >
                        <PlayerCards cards={firstGameStage >= 2 ? myPlayer?.cards : []} />
                      </View>
                      <View
                        style={styles.firstRow}
                        id="player-hand-wrapper"
                        onLayout={(e) => register('myFirstGame.playerHand', e)}
                      >
                        <PlayerInfo />
                      </View>
                      <View style={[styles.secondRow, { flexDirection: 'row', marginTop: 16 }]}>
                        <DS.Frame style={[styles.secondRow, { flexDirection: 'row', gap: 16 }]}>
                          {myPlayer?.actions.map((action, idx) => {
                            const actionConfig = ActionColorAndIcons[action] || { type: 'On Light' }
                            return (
                              <React.Fragment key={`action-${action}-${idx}`}>
                                <DS.Button
                                  numberOfLines={1}
                                  variant="Large/Primary"
                                  type={actionConfig.type}
                                  disabled={
                                    action !== ActionHint.CALL || !stagesWhereCallIsEnabled.includes(stageInternalName)
                                  }
                                  onPress={() => {
                                    goToNextFirstGameStep()
                                  }}
                                  maxWidth
                                  rightIcon={ActionColorAndIcons[action]?.icon}
                                  style={[
                                    action === ActionHint.FOLD ? { backgroundColor: DS.palette['Brand/Maroon'] } : {},
                                    action === ActionHint.CALL && stagesWhereCallIsEnabled.includes(stageInternalName)
                                      ? { borderColor: DS.palette['Brand/Purple'], borderWidth: 2 }
                                      : {}
                                  ]}
                                >
                                  {getActionText(action)}
                                </DS.Button>
                                {action === ActionHint.CALL && stagesWhereCallIsEnabled.includes(stageInternalName) ? (
                                  <CallAmountIndicator />
                                ) : null}
                              </React.Fragment>
                            )
                          })}
                        </DS.Frame>
                      </View>
                    </>
                  </View>

                  <BottomSheetModal
                    ref={bottomSheetModalRef}
                    overrideReduceMotion={ReduceMotion.System}
                    index={0}
                    snapPoints={snapPoints}
                    backdropComponent={renderBackdrop}
                    onDismiss={closeBottomModal}
                    enableDynamicSizing={false}
                    overDragResistanceFactor={3}
                    detached={false}
                    enableDismissOnClose={enableDismissOnClose}
                    enableBlurKeyboardOnGesture={false}
                    enableContentPanningGesture={false}
                    enableHandlePanningGesture={true}
                    enableOverDrag={false}
                    enablePanDownToClose={false}
                    keyboardBehavior={Platform.OS === 'android' ? 'extend' : 'interactive'}
                    keyboardBlurBehavior="none"
                    android_keyboardInputMode="adjustResize"
                    handleStyle={styles.bottomSheetHandleStyle}
                    handleIndicatorStyle={
                      bottomModalContent?.kind === BottomModalTypes.GameStart
                        ? { display: 'none' }
                        : styles.bottomSheetIndicatorStyle
                    }
                    backgroundStyle={{ backgroundColor: palette.cream }}
                    containerStyle={{
                      zIndex: 99
                    }}
                    style={{
                      marginHorizontal: isWeb ? 'auto' : undefined,
                      maxWidth: maxWidth,
                      width: '100%'
                    }}
                  >
                    <BottomModalContent config={bottomModalContent} />
                  </BottomSheetModal>
                </DS.Frame>
              )}
              {firstGameStage >= MyFirstGameSteps.length - 1 && <MyFirstGameWinScreen />}
            </DS.Frame>
          </DS.Frame>
        </WebWrapper>
      </LayoutShiftProvider>
    </DS.Frame>
  )
})

export default MyFirstGameScreen
